<template>
    <div class="scheduler-backdrop" @click.self="closeModal">
        <div class="scheduler-shell">
            <div class="scheduler-modal" role="dialog" aria-labelledby="schedulerTitle">
                <header class="scheduler-header">
                    <div class="scheduler-header__title">
                        <span class="scheduler-header__icon" aria-hidden="true">
                            <i class="fa fa-calendar-check"></i>
                        </span>
                        <div>
                            <h1 id="schedulerTitle" class="scheduler-header__h1">Scheduler</h1>
                            <p class="scheduler-header__sub">Plan a task or schedule an event for your team</p>
                        </div>
                    </div>
                    <div class="scheduler-header__actions">
                        <button
                            v-if="canShowDelete"
                            :disabled="loading"
                            class="scheduler-icon-btn scheduler-icon-btn--danger"
                            title="Delete"
                            aria-label="Delete"
                            @click="handleMarkDoneSave('inactive')"
                        >
                            <i class="fa fa-trash"></i>
                        </button>
                        <button
                            type="button"
                            class="scheduler-icon-btn"
                            title="Close"
                            aria-label="Close"
                            @click="closeModal"
                        >
                            <i class="fa fa-times"></i>
                        </button>
                    </div>
                </header>

                <div class="scheduler-tabs" role="tablist">
                    <button
                        v-for="tab in allTabs"
                        :key="tab.id"
                        type="button"
                        role="tab"
                        :aria-selected="activeTab.id == tab.id"
                        :class="['scheduler-tab', { 'scheduler-tab--active': activeTab.id == tab.id }]"
                        @click="setActiveTab(tab)"
                    >
                        {{ tab.title }}
                    </button>
                </div>

                <div class="scheduler-body">
                    <component
                        :is="availableTabComponents[activeTab.id]"
                        :startDate="startDate"
                        :endDate="endDate"
                        :editData="editData"
                        :taskStatus="taskStatus"
                        :workStatus="workStatus"
                        :markAsDoneShow="markAsDoneShow"
                        :isCompleted="isCompleted"
                        :tagList="tagList"
                        :priorityList="priorityList"
                        :dealData="dealData"
                        :dealId="dealId"
                        :dealCustomer="dealCustomer"
                        :isFromDeal="isFromDeal"
                        :isNested="isNested"
                        :reloadMinuteTrigger="reloadMinuteCounter"
                        @handleSubmit="handleSave"
                        @openScheduler="handleOpenChildScheduler"
                        @tabChange="handleChildTabChange"
                        ref="childRef"
                    />
                </div>

                <footer
                    v-if="activeInnerTab === 'work_status'"
                    class="scheduler-footer"
                >
                    <div class="scheduler-footer__group scheduler-footer__group--left">
                        <button
                            v-if="canShowMarkAsDone"
                            :disabled="loading"
                            class="scheduler-btn scheduler-btn--success"
                            @click="handleMarkDoneSave('completed')"
                        >
                            <i class="fa fa-check"></i>
                            <span>Make Done</span>
                        </button>

                        <button
                            v-if="canShowApproveStatus || canShowApprove"
                            class="scheduler-btn scheduler-btn--success"
                            @click="handleApproved()"
                        >
                            <i :class="canShowApproveStatus ? 'fa fa-check' : 'fa fa-thumbs-up'"></i>
                            <span>{{ canShowApproveStatus ? 'Approved' : 'Approve' }}</span>
                        </button>

                        <button
                            v-if="canShowWorkStatusUpdate"
                            :disabled="loading"
                            class="scheduler-btn scheduler-btn--info"
                            @click="handleWorkStatusSave()"
                        >
                            <i class="fa fa-sync"></i>
                            <span>Update Work Status</span>
                        </button>
                    </div>

                    <div class="scheduler-footer__group scheduler-footer__group--right">
                        <button
                            v-if="canShowSave"
                            class="scheduler-btn scheduler-btn--primary"
                            :disabled="loading"
                            @click="handleSave"
                        >
                            <i class="fa fa-save"></i>
                            <span>Save</span>
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    </div>

    <WorkStatusTimeSheetModal
        v-if="store.state.isModalOpenTwo"
        :workStatusTimeSheet="workStatusTimeSheet"
        :canApprove="canApproveWorkStatus"
        :loading="loading"
        :taskTitle="taskTitle"
        @handleApproveAll="handleApproveAll"
        @handleRowAction="handleWorkStatusRowAction"
      />

    <TaskEventScheduler
        v-if="isChildSchedulerOpen"
        :startDate="startDate"
        :endDate="endDate"
        :editData="childEditData"
        :taskStatus="childTaskStatus"
        :workStatus="{}"
        :dealCustomer="dealCustomer"
        :isNested="true"
        @submitted="onChildSchedulerSubmitted"
      />
</template>

<script setup>
import {computed, inject, nextTick, onMounted, ref} from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import Task from "./scheduler/Task.vue";
import Event from "./scheduler/Event.vue";
import WorkStatusTimeSheetModal from "./scheduler/WorkStatusTimeSheetModal.vue";
import handleTagRegister from "@/services/modules/scm/tag-register";
import handleActivity from "@/services/modules/procurement/activity";
import handleHospital from '@/services/modules/hospital'
import useDate from '@/services/utils/day'

const props = defineProps({
    startDate: String,
    endDate: String,
    editData: {
      type:Object,
      default: {}
    },
    taskStatus: {
      type:Object,
      default: {}
    },
    workStatus: {
      type:Object,
      default: {}
    },
    markAsDoneShow:{
      type:Boolean,
      default: false
    },
    isCompleted:{
      type:Boolean,
      default: false
    },
    companyRoles: {
        type: Array,
        default: () => []
    },
    dealId: {
      type: Number,
      default: null
    },
    dealCustomer: {
        type: Object,
        required: true
    },
    dealData: {
      type: Object,
      default: {}
    },
    isFromDeal: {
      type: Boolean,
      default: false
    },
    isNested: {
      type: Boolean,
      default: false
    }
})

const emit = defineEmits(['submitted'])
const store = useStore();
const route = useRoute();

const showError = inject('showError');
const showSuccess = inject('showSuccess');

const childRef = ref(null);
const loading = ref(false);
const tagList = ref([]);
const priorityList = ref([])
const workStatusTimeSheet = ref([])
const isChildSchedulerOpen = ref(false);
const childEditData = ref({});
const childTaskStatus = ref({});
const childMinuteId = ref(null);
const activeInnerTab = ref('work_status');
const reloadMinuteCounter = ref(0);

const handleChildTabChange = (tab) => {
  activeInnerTab.value = tab;
}

const canApproveWorkStatus = computed(() => {
  const ts = props.taskStatus || {};
  if (ts.can_approve !== undefined) return ts.can_approve
  return ! ! ts.report_to
});

const canShowMarkAsDone = computed(() =>
    props.markAsDoneShow && !props.isCompleted && props.taskStatus.assigned_to
);

const canShowApprove = computed(() =>
    props.markAsDoneShow && props.isCompleted && canApproveWorkStatus.value && props.editData.status !== 'approved'
);

const canShowApproveStatus = computed(() =>
    props.markAsDoneShow && props.isCompleted && canApproveWorkStatus.value && props.editData.status == 'approved'
);

const canShowDelete = computed(() =>
    !props.isCompleted && props.taskStatus.is_created
);

const isWorkStatusLocked = computed(() => {
  if (! props.workStatus) return false
  return ['approved', 'rejected'].includes(props.workStatus.status)
});

const canShowWorkStatusUpdate = computed(() =>
    props.workStatus && Object.keys(props.workStatus).length > 0 && ! isWorkStatusLocked.value
);

const taskTitle = computed(() => {
  if (! props.editData) return ''
  return props.editData.short_description || ''
});

const hasReportTo = computed(() => {
  return  props.editData && props.editData.values.some(value => value.field_key === 'report_to');
});

const canShowSave = computed(() => {
  const ts = props.taskStatus || {};
  const hasPermission =
      ts.report_to || ts.assigned_to || ts.created_by || !ts.participant;
  const isSaveAllowed =
      !props.isCompleted || (props.isCompleted && ts.report_to) || Object.keys(ts).length === 0;

  return isSaveAllowed && hasPermission;
});

const {fetchTagRegisterList} = handleTagRegister()
const {
  storeTaskActivityLog,
  updateTaskActivityLog,
  updateMarkAsDoneTaskActivityLog,
  updateActivityLogWorkStatus,
  fetchTaskActivityWorkStatusLog,
  updateWorkStatusApproval,
  approveAllWorkStatus,
  fetchSingleTaskActivityLog,
  updateActivityMinute
} = handleActivity()
const { fetchNoteMasterList } = handleHospital()
const { formatDateTime, formatToDate } = useDate()

const tabs = [
    { id: 'task', title: 'Task', ref: 'taskRef' },
    { id: 'event', title: 'Event', ref: 'eventRef' },
];

const allTabs = computed(() => {
  if (props.editData && props.editData.type) {
    return tabs.filter(tab => tab.id === props.editData.type);
  }
  return tabs;
});

const tabComponents = {
  'task': Task,
  'event': Event
};

const availableTabComponents = computed(() => {
  const result = {};
  allTabs.value.forEach(tab => {
    if (tabComponents[tab.id]) {
      result[tab.id] = tabComponents[tab.id];
    }
  });
  return result;
});

const activeTab = ref(allTabs.value && allTabs.value[0]);

const setActiveTab = (tab) => {
    activeTab.value = tab;
};

const refreshWorkStatusTimeSheet = (activityId, openModal = false) => {
  let query = `?company_id=${route.params.companyId}`;

  return fetchTaskActivityWorkStatusLog(activityId, query)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        workStatusTimeSheet.value = res.data;

        if (openModal) {
          store.state.isModalOpenTwo = true;
        }
      })
}

const handleApproved = () => {
  const data = childRef.value.getMarkDoneFormData();

  if (!data.id){
    showError("Data No Found")
    return
  }

  loading.value = true;

  refreshWorkStatusTimeSheet(data.id, true)
      .catch(() => {
        showError("Something went wrong")
      })
      .finally(() => {
        loading.value = false
      })
}

const handleApproveAll = () => {
  const data = childRef.value.getMarkDoneFormData();

  if (!data.id){
    showError("Data No Found")
    return
  }

  loading.value = true;

  approveAllWorkStatus({ company_id: route.params.companyId }, data.id)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        emit('submitted')
        return refreshWorkStatusTimeSheet(data.id)
      })
      .catch(() => {
        showError("Something went wrong")
      })
      .finally(() => {
        loading.value = false
      })
}

const handleWorkStatusRowAction = (payload) => {
  const data = childRef.value.getMarkDoneFormData();

  if (!data.id){
    showError("Data No Found")
    return
  }

  loading.value = true;

  let formattedData = {
    company_id: route.params.companyId,
    status: payload.status,
    comment: payload.comment
  }

  updateWorkStatusApproval(formattedData, payload.id)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        emit('submitted')
        return refreshWorkStatusTimeSheet(data.id)
      })
      .catch(() => {
        showError("Something went wrong")
      })
      .finally(() => {
        loading.value = false
      })
}

const handleMarkDoneSave = (status) => {
  if (status === 'inactive' && !window.confirm("Are you sure you want to delete this task?")) {
    return;
  }

  loading.value = true;
  let companyId = route.params.companyId;
  const data = childRef.value.getMarkDoneFormData();

  let formattedData = {
    id: data.id,
    status: status,
    company_id: companyId
  }

  if (!formattedData.id){
    showError("Data No Found")
    return
  }

  updateMarkAsDoneTaskActivityLog(formattedData, data.id)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        emit('submitted')
        closeModal()
      })
      .catch(() => {
        showError("Something went wrong")
      }).finally(() => {
    loading.value = false
  })
}

const handleWorkStatusSave = () => {
  const data = childRef.value.getWorkStatusFormData();

  if (['approved', 'rejected'].includes(data.status)) {
    showError("This work status can no longer be updated")
    return
  }

  loading.value = true;
  let companyId = route.params.companyId;

  let formattedData = {
    id: data.id,
    area_id: data.area_id ? data.area_id.id : null,
    hours: data.hours,
    date: data.date,
    conveyance: data.conveyance,
    description: data.description,
    company_id: companyId,
  }

  if (!formattedData.id){
    showError("Data No Found")
    return
  }

  updateActivityLogWorkStatus(formattedData, data.id)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        emit('submitted')
      })
      .catch(() => {
        showError("Something went wrong")
      }).finally(() => {
    loading.value = false
  })
}

const handleSave = () => {
    loading.value = true;
    let companyId = route.params.companyId;
    let query = '?company_id=' + companyId;
    const data = childRef.value.getFormData();

    const formattedData = {
      short_description: data.title,
      start_time: data.start_time ? formatDateTime(data.start_time) : null,
      end_time: data.end_time ? formatDateTime(data.end_time) : null,
      date: data.start_time ? formatToDate(data.start_time) : null,
      assigned_to: data.assign_to ? data.assign_to.id : null,
      report_to: data.report_to ? data.report_to.id : null,
      area_id: data.area_id ? data.area_id.id : null,
      participant_ids: data.participants ? data.participants.map(p => p.id) : [],
      activity_setup_id: data.activity_setup_id,
      tags: data.tag ? data.tag.map(t => t.id) : [],
      project_id: data.project ? data.project.id : null,
      project_activity_id: data.project_activity ? data.project_activity.id : null,
      full_description: data.description,
      type: data.type,
      client_id: data.client ? data.client.id : null,
      priority_id: data.priority ? data.priority.id : null,
      deal_id: data.deal ? data.deal.id : null,
      route_name: data.route_name,
      company_id: companyId,
      parent_activity_log_id: data.parent_activity_log_id
    };

    const formData = new FormData();
    Object.entries(formattedData).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item, i) => formData.append(`${key}[${i}]`, item));
        return;
      }
      if (value === null || value === undefined) {
        formData.append(key, '');
        return;
      }
      formData.append(key, value);
    });

      const attachments = data.attachments;
      if (attachments && attachments.length) {
        handleAttachments(formData, attachments);
      }

   if (!data.id){
     storeTaskActivityLog(formData, query)
         .then(res => {
           if (!res.status) {
             showError(res.message)
             return
           }
           showSuccess(res.message)
           closeModal(res.data)
         })
         .catch(() => {
           showError("Something went wrong")
         }).finally(() => {
       loading.value = false
     })
   }

   if (data.id){
     updateTaskActivityLog(formData, data.id)
         .then(res => {
           if (!res.status) {
             showError(res.message)
             return
           }
           showSuccess(res.message)
           closeModal()
         })
         .catch(() => {
           showError("Something went wrong")
         }).finally(() => {
       loading.value = false
     })
   }
}

const handleAttachments = (formData, attachments) =>  {
  const previousFileIds = attachments
      .filter(attachment => typeof attachment === 'object' && attachment.hasOwnProperty('id'))
      .map(attachment => attachment.id);

  attachments.forEach((attachment, index) => {
    if (typeof attachment !== 'object' || !attachment.hasOwnProperty('id')) {
      formData.append(`attachments[${index}]`, attachment);
    }
  });

  if (previousFileIds.length) {
    formData.append('previous_file_ids', JSON.stringify(previousFileIds));
  }
}

onMounted(() => {
  let companyQuery = '?company_id=' + route.params.companyId;

  fetchTagRegisterList(companyQuery).then(res => {
    if (res.data) {
      tagList.value = res.data;
    }
    let noteMasterQuery = companyQuery + '&is_all=true&status=active&note_type=priority'
    fetchNoteMasterList(noteMasterQuery).then(res => {
      if (res.data) {
        priorityList.value = res.data
      }
    });
  })
  })

const closeModal = (createdData) => {
    if (props.isNested) {
      emit('submitted', createdData);
      return;
    }
    childRef.value && childRef.value.resetForm();
    store.state.isModalOpen = false;
    store.state.isModalOpenTwo = false;
    props.markAsDoneShow = false;
    props.isCompleted = false
    emit('submitted')
  }

const handleOpenChildScheduler = (payload) => {
  childMinuteId.value = payload.minute_id;

  if (! payload.linked_activity_log_id) {
    childEditData.value = {
      short_description: payload.note,
      parent_activity_log_id: payload.parent_activity_log_id,
      values: []
    };
    childTaskStatus.value = {};
    isChildSchedulerOpen.value = true;
    return;
  }

  const query = `?company_id=${route.params.companyId}`;
  fetchSingleTaskActivityLog(payload.linked_activity_log_id, query).then(res => {
    if (! res.status) {
      showError(res.message);
      return;
    }
    childEditData.value = res.data;
    childTaskStatus.value = res.task_status;
    isChildSchedulerOpen.value = true;
  });
}

const reloadParentMinutes = () => {
  reloadMinuteCounter.value = reloadMinuteCounter.value + 1;
}

const onChildSchedulerSubmitted = (createdData) => {
  nextTick(() => {
    isChildSchedulerOpen.value = false;
  });

  if (! createdData || ! createdData.id) {
    reloadParentMinutes();
    return;
  }

  updateActivityMinute({
    company_id: route.params.companyId,
    note: childEditData.value.short_description,
    linked_activity_log_id: createdData.id
  }, childMinuteId.value)
      .then(res => {
        if (! res.status) {
          showError(res.message);
          return;
        }
        reloadParentMinutes();
      })
      .catch(() => {
        showError("Something went wrong");
      });
}

</script>


<style scoped>
.scheduler-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(20, 24, 40, 0.45);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 1050;
    overflow-y: auto;
}
.scheduler-shell {
    min-height: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 32px 16px;
    box-sizing: border-box;
}
.scheduler-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--sched-border, #e6e9ef);
    background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
}
.scheduler-header__title {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    flex: 1 1 auto;
}
.scheduler-header__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: var(--sched-accent-soft, #eef0ff);
    color: var(--sched-accent, #6366f1);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.scheduler-header__h1 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: var(--sched-text, #1f2a44);
}
.scheduler-header__sub {
    margin: 2px 0 0;
    font-size: 0.8125rem;
    color: var(--sched-muted, #6b7691);
}
.scheduler-header__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}
.scheduler-icon-btn {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid var(--sched-border, #e6e9ef);
    background: #ffffff;
    color: var(--sched-muted, #6b7691);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 160ms ease, color 160ms ease, border-color 160ms ease, transform 160ms ease;
}
.scheduler-icon-btn:hover:not(:disabled) {
    background: var(--sched-surface, #f7f9fc);
    color: var(--sched-text, #1f2a44);
}
.scheduler-icon-btn:focus-visible {
    outline: 2px solid var(--sched-accent, #6366f1);
    outline-offset: 2px;
}
.scheduler-icon-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}
.scheduler-icon-btn--danger {
    color: var(--sched-danger, #ef4444);
    border-color: #fde2e2;
    background: #fff5f5;
}
.scheduler-icon-btn--danger:hover:not(:disabled) {
    background: var(--sched-danger, #ef4444);
    color: #ffffff;
    border-color: var(--sched-danger, #ef4444);
}
.scheduler-tabs {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 12px 24px;
    background: var(--sched-bg, #ffffff);
    border-bottom: 1px solid var(--sched-border, #e6e9ef);
}
.scheduler-tab {
    appearance: none;
    border: none;
    background: transparent;
    color: var(--sched-muted, #6b7691);
    font-size: 0.8125rem;
    font-weight: 600;
    padding: 7px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: color 160ms ease, background 160ms ease, box-shadow 160ms ease;
}
.scheduler-tab:hover:not(.scheduler-tab--active) {
    color: var(--sched-text, #1f2a44);
    background: rgba(99, 102, 241, 0.06);
}
.scheduler-tab--active {
    color: var(--sched-accent, #6366f1);
    background: var(--sched-accent-soft, #eef0ff);
    box-shadow: 0 1px 2px rgba(20, 24, 40, 0.04);
}
.scheduler-tab:focus-visible {
    outline: 2px solid var(--sched-accent, #6366f1);
    outline-offset: 2px;
}
.scheduler-body {
    padding: 16px 8px;
    background: var(--sched-bg, #ffffff);
}
.scheduler-body :deep(.modal-body) {
    padding: 0 16px;
}
.scheduler-body :deep(.row) {
    margin-left: 0;
    margin-right: 0;
}
.scheduler-body :deep(.form-control),
.scheduler-body :deep(.v-select),
.scheduler-body :deep(input[type="datetime-local"]),
.scheduler-body :deep(input[type="text"]) {
    max-width: 100%;
    min-width: 0;
}
.scheduler-body :deep(.col-1),
.scheduler-body :deep(.col-2),
.scheduler-body :deep(.col-3),
.scheduler-body :deep(.col-4),
.scheduler-body :deep(.col-5),
.scheduler-body :deep(.col-6),
.scheduler-body :deep(.col-7),
.scheduler-body :deep(.col-8),
.scheduler-body :deep(.col-9),
.scheduler-body :deep(.col-10),
.scheduler-body :deep(.col-11),
.scheduler-body :deep(.col-12),
.scheduler-body :deep(.col-sm-6),
.scheduler-body :deep(.col-sm-12),
.scheduler-body :deep(.col-md-4),
.scheduler-body :deep(.col-md-6),
.scheduler-body :deep(.col-md-8),
.scheduler-body :deep(.col-md-12),
.scheduler-body :deep(.col-lg-6),
.scheduler-body :deep(.col-lg-12) {
    padding-left: 8px;
    padding-right: 8px;
}
.scheduler-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 24px;
    background: var(--sched-surface, #f7f9fc);
    border-top: 1px solid var(--sched-border, #e6e9ef);
    flex-wrap: wrap;
}
.scheduler-footer__group {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.scheduler-footer__group--right {
    margin-left: auto;
}
.scheduler-btn {
    appearance: none;
    border: 1px solid transparent;
    border-radius: 999px;
    font-size: 0.8125rem;
    font-weight: 600;
    padding: 8px 16px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    line-height: 1.2;
    white-space: nowrap;
    transition: background 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}
.scheduler-btn:focus-visible {
    outline: 2px solid var(--sched-accent, #6366f1);
    outline-offset: 2px;
}
.scheduler-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.scheduler-btn:not(:disabled):active {
    transform: translateY(1px);
}
.scheduler-btn--primary {
    color: #ffffff;
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    box-shadow: 0 4px 12px -4px rgba(79, 70, 229, 0.45);
}
.scheduler-btn--primary:hover:not(:disabled) {
    box-shadow: 0 8px 18px -6px rgba(79, 70, 229, 0.55);
    transform: translateY(-1px);
}
.scheduler-btn--success {
    color: #ffffff;
    background: var(--sched-success, #16a34a);
}
.scheduler-btn--success:hover:not(:disabled) {
    background: #15803d;
}
.scheduler-btn--info {
    color: #ffffff;
    background: var(--sched-info, #0ea5e9);
}
.scheduler-btn--info:hover:not(:disabled) {
    background: #0284c7;
}
.scheduler-modal {
    width: 100%;
    max-width: 1140px;
    margin: 0 auto;
    background: var(--sched-bg, #ffffff);
    color: var(--sched-text, #1f2a44);
    border-radius: var(--sched-radius, 14px);
    border: 1px solid var(--sched-border, #e6e9ef);
    box-shadow: var(--sched-shadow, 0 20px 60px -20px rgba(50, 70, 110, 0.25), 0 8px 20px -10px rgba(50, 70, 110, 0.12));
    display: flex;
    flex-direction: column;
    overflow: visible;
}
.scheduler-modal,
.scheduler-modal * {
    box-sizing: border-box;
}
.scheduler-modal :deep(.d-xl-none),
.scheduler-modal :deep(.d-lg-none),
.scheduler-modal :deep(.d-md-none),
.scheduler-modal :deep(.d-sm-none) {
    display: none;
}
@media (min-width: 576px) {
    .scheduler-modal {
        max-width: 540px;
    }
    .scheduler-footer__group--right {
        margin-left: auto;
    }
}
@media (min-width: 768px) {
    .scheduler-shell {
        padding: 32px 24px;
    }
    .scheduler-modal {
        max-width: 720px;
    }
    .scheduler-header {
        padding: 20px 24px;
    }
    .scheduler-tabs {
        padding: 12px 24px;
    }
    .scheduler-body {
        padding: 20px 24px;
    }
    .scheduler-body :deep(.modal-body) {
        padding: 0 16px;
    }
    .scheduler-footer {
        padding: 14px 24px;
    }
    .scheduler-footer__group--right {
        margin-left: auto;
        width: auto;
        justify-content: flex-end;
    }
}
@media (min-width: 992px) {
    .scheduler-modal {
        max-width: 960px;
    }
}
@media (min-width: 1200px) {
    .scheduler-modal {
        max-width: 1140px;
    }
}
@media (max-width: 767.98px) {
    .scheduler-backdrop {
        overflow: hidden;
    }
    .scheduler-shell {
        padding: 0;
        min-height: 0;
        height: 100%;
    }
    .scheduler-modal {
        max-width: 100%;
        height: 100vh;
        height: 100dvh;
        border: 0;
        border-radius: 0;
        overflow: hidden;
    }
    .scheduler-header,
    .scheduler-tabs,
    .scheduler-footer {
        flex-shrink: 0;
    }
    .scheduler-header {
        padding: 14px 16px;
        gap: 12px;
    }
    .scheduler-header__sub {
        display: none;
    }
    .scheduler-header__icon {
        width: 40px;
        height: 40px;
        font-size: 16px;
    }
    .scheduler-header__h1 {
        font-size: 1.05rem;
    }
    .scheduler-tabs {
        padding: 10px 12px;
        gap: 4px;
        flex-wrap: wrap;
    }
    .scheduler-tab {
        flex: 1 1 0;
        text-align: center;
        padding: 8px 12px;
        font-size: 0.8rem;
    }
    .scheduler-body {
        flex: 1 1 auto;
        min-height: 0;
        max-height: none;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
        padding: 12px 8px 20px;
    }
    .scheduler-body :deep(.modal-body) {
        padding: 0 12px;
    }
    .scheduler-body :deep(.row) {
        margin-left: 0;
        margin-right: 0;
    }
    .scheduler-body :deep(.col-12),
    .scheduler-body :deep(.col-sm-12),
    .scheduler-body :deep(.col-md-4),
    .scheduler-body :deep(.col-md-6),
    .scheduler-body :deep(.col-md-8),
    .scheduler-body :deep(.col-md-12),
    .scheduler-body :deep(.col-lg-6),
    .scheduler-body :deep(.col-lg-12) {
        padding-left: 8px;
        padding-right: 8px;
    }
    .scheduler-footer {
        flex-direction: column;
        align-items: stretch;
        flex-wrap: nowrap;
        gap: 8px;
        padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
        box-shadow: 0 -6px 16px -10px rgba(20, 24, 40, 0.25);
    }
    .scheduler-footer__group {
        width: 100%;
        flex-wrap: nowrap;
    }
    .scheduler-footer__group:empty {
        display: none;
    }
    .scheduler-footer__group--left .scheduler-btn {
        flex: 1 1 0;
        min-width: 0;
        min-height: 40px;
        justify-content: center;
    }
    .scheduler-footer__group--right {
        margin-left: 0;
    }
    .scheduler-footer__group--right .scheduler-btn {
        width: 100%;
        min-height: 42px;
        justify-content: center;
    }
    .scheduler-btn {
        padding: 8px 12px;
        font-size: 0.8rem;
    }
    .scheduler-btn span {
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .scheduler-icon-btn {
        width: 40px;
        height: 40px;
    }
}
@media (max-width: 575.98px) {
    .scheduler-header {
        padding: 12px 14px;
    }
    .scheduler-header__h1 {
        font-size: 0.95rem;
    }
    .scheduler-header__icon {
        width: 36px;
        height: 36px;
        font-size: 14px;
        border-radius: 10px;
    }
    .scheduler-tabs {
        padding: 8px 10px;
    }
    .scheduler-tab {
        padding: 8px 10px;
        font-size: 0.78rem;
    }
    .scheduler-body {
        padding: 8px 4px 20px;
    }
    .scheduler-btn {
        padding: 8px 10px;
        font-size: 0.78rem;
        gap: 6px;
    }
}
@media (max-width: 374.98px) {
    .scheduler-header__actions {
        gap: 4px;
    }
    .scheduler-icon-btn {
        width: 36px;
        height: 36px;
    }
    .scheduler-btn {
        padding: 8px;
        font-size: 0.78rem;
        gap: 4px;
    }
}
@media (prefers-reduced-motion: reduce) {
    .scheduler-btn,
    .scheduler-icon-btn,
    .scheduler-tab {
        transition: none;
    }
}
</style>